import React from "react";
import { format, parseISO } from "date-fns";
import { 
  CircleCheckBig,
  MoreVertical, 
  Archive, 
  Trash2, 
  Type, 
  User, 
  Users, 
  Calendar as CalendarIcon, 
  Layout, 
  AlertCircle
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { BoardState, Task } from "./types";

const PRIORITY_BADGE: Record<string, string> = {
  low: "bg-secondary text-secondary-foreground",
  medium: "bg-info/20 text-info",
  high: "bg-warning/20 text-warning",
  critical: "bg-destructive/20 text-destructive",
};

const PRIORITY_DOT: Record<string, string> = {
  low: "bg-secondary",
  medium: "bg-info",
  high: "bg-warning",
  critical: "bg-destructive",
};

interface BoardListViewProps {
  state: BoardState;
  onTaskClick: (task: Task) => void;
  onTaskUpdate?: (taskId: string, updates: Partial<Task>) => void;
  onArchive?: (taskId: string) => void;
  onDelete?: (taskId: string) => void;
  searchTerm?: string;
  enableUpvotes?: boolean;
  onToggleUpvote?: (taskId: string) => void;
  currentUserId?: string;
}

interface FlatTask {
  task: Task;
  index: number;
  columnTitle: string;
  columnColor?: string;
}

const getColumnBadgeStyle = (bgColor?: string): string => {
  if (!bgColor) return "bg-muted text-muted-foreground";
  const lower = bgColor.toLowerCase();

  if (lower.includes("blue") || lower.includes("info")) {
    return "bg-board-column-blue text-info border border-info/20";
  }
  if (lower.includes("green") || lower.includes("success")) {
    return "bg-board-column-green text-success border border-success/20";
  }
  if (lower.includes("red") || lower.includes("destructive")) {
    return "bg-board-column-red text-destructive border border-destructive/20";
  }
  if (lower.includes("orange") || lower.includes("yellow") || lower.includes("warning")) {
    return "bg-board-column-yellow text-warning border border-warning/20";
  }
  if (lower.includes("accent") || lower.includes("brand")) {
    return "bg-board-column-primary text-primary border border-primary/20";
  }

  return "bg-muted text-muted-foreground";
};

export const BoardListView: React.FC<BoardListViewProps> = ({
  state,
  onTaskClick,
  onTaskUpdate,
  onArchive,
  onDelete,
  searchTerm,
  enableUpvotes,
  onToggleUpvote,
  currentUserId,
}) => {
  const formatDate = (iso?: string | null) => {
    if (!iso) return null;
    try { return format(parseISO(iso), "MMM d, yyyy"); } catch { return null; }
  };

  const flatTasks: FlatTask[] = [];
  let index = 1;
  state.columnOrder.forEach(colId => {
    const taskIds = state.columns[colId] || [];
    const colMeta = state.columnMeta[colId];
    taskIds.forEach(taskId => {
      const task = state.tasksById[taskId];
      if (task) {
        flatTasks.push({
          task,
          index: index++,
          columnTitle: colMeta?.title || colId,
          columnColor: colMeta?.bgColor,
        });
      }
    });
  });

  const visibleTasks = React.useMemo(() => {
    if (!searchTerm?.trim()) return flatTasks;
    const term = searchTerm.toLowerCase();
    return flatTasks.filter(({ task, columnTitle }) => (
      task.title?.toLowerCase().includes(term) ||
      task.description?.toLowerCase().includes(term) ||
      columnTitle.toLowerCase().includes(term) ||
      task.assignees?.some(a => a.name.toLowerCase().includes(term))
    ));
  }, [flatTasks, searchTerm]);

  return (
    <div className="rounded-xl border border-border overflow-hidden bg-white dark:bg-card">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-muted-foreground">
            <th className="w-10 px-2 py-3 text-center">
              <span className="sr-only">Complete</span>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold select-none w-[32%]">
              <div className="flex items-center gap-1.5">
                <Type className="w-3.5 h-3.5 text-muted-foreground/70" />
                Name
              </div>
            </th>
            <th className="px-4 py-3 text-center text-xs font-semibold select-none w-32">
              <div className="flex items-center justify-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-muted-foreground/70" />
                Priority
              </div>
            </th>
            <th className="px-4 py-3 text-center text-xs font-semibold select-none w-36">
              <div className="flex items-center justify-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground/70" />
                Due Date
              </div>
            </th>
            <th className="px-4 py-3 text-center text-xs font-semibold select-none w-36">
              <div className="flex items-center justify-center gap-1.5">
                <Layout className="w-3.5 h-3.5 text-muted-foreground/70" />
                Status
              </div>
            </th>
            <th className="px-4 py-3 text-center text-xs font-semibold select-none w-40">
              <div className="flex items-center justify-center gap-1.5">
                <User className="w-3.5 h-3.5 text-muted-foreground/70" />
                Created By
              </div>
            </th>
            <th className="px-4 py-3 text-center text-xs font-semibold select-none w-44">
              <div className="flex items-center justify-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-muted-foreground/70" />
                Assignee
              </div>
            </th>
            {enableUpvotes && (
              <th className="px-4 py-3 text-center text-xs font-semibold select-none w-20">
                <div className="flex items-center justify-center gap-1.5">
                  <svg
                    viewBox="0 0 24 22"
                    fill="none"
                    className="w-3 h-3 text-muted-foreground/70"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 4.5l7.5 13a1.5 1.5 0 0 1-1.3 2.2H5.8a1.5 1.5 0 0 1-1.3-2.2L12 4.5z" />
                  </svg>
                  Upvotes
                </div>
              </th>
            )}
            <th className="w-12 px-2 py-3 text-center">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleTasks.length === 0 ? (
            <tr>
              <td colSpan={enableUpvotes ? 9 : 8} className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                {searchTerm?.trim() ? "No matching tasks found." : "No tasks yet. Create one to get started."}
              </td>
            </tr>
          ) : (
            visibleTasks.map(({ task, columnTitle, columnColor }, i) => (
              <tr
                key={task.id}
                onClick={() => onTaskClick(task)}
                className={`group cursor-pointer transition-all duration-200 hover:bg-sidebar-accent/50 dark:hover:bg-white/[0.04] ${i !== visibleTasks.length - 1
                    ? "border-b border-border/60"
                    : ""
                  }`}
              >
                <td className="px-2 py-3 text-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTaskUpdate?.(task.id, { 
                        completed: !task.completed,
                        completedAt: !task.completed ? new Date().toISOString() : null 
                      });
                    }}
                    className={`transition-colors focus:outline-none ${task.completed ? "text-success" : "text-gray-300 dark:text-gray-600 hover:text-gray-400"}`}
                  >
                    <CircleCheckBig className={`w-5 h-5 transition-colors ${task.completed ? "text-green-500" : "text-muted-foreground/40 hover:text-muted-foreground/60"}`} />
                  </button>
                </td>
                {/* Name */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[task.priority] || "bg-gray-400"}`}
                    />
                    <span className="font-medium text-gray-800 dark:text-gray-100 truncate">
                      {task.title}
                    </span>
                  </div>
                </td>
                {/* Priority */}
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium capitalize ${PRIORITY_BADGE[task.priority] || "bg-gray-100 text-gray-600"}`}
                    >
                      {task.priority}
                    </span>
                  </div>
                </td>
                {/* Due Date */}
                <td className="px-4 py-3 text-center text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  {formatDate(task.dueDate) || (
                    <span className="text-gray-300 dark:text-gray-600">—</span>
                  )}
                </td>
                {/* Status */}
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${getColumnBadgeStyle(columnColor)}`}
                    >
                      {columnTitle}
                    </span>
                  </div>
                </td>
                {/* Created By */}
                <td className="px-4 py-3 text-center">
                  {task.creator ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-muted overflow-hidden flex-shrink-0">
                        {task.creator.avatar ? (
                          <img src={task.creator.avatar} alt={task.creator.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="w-full h-full flex items-center justify-center text-[10px] font-semibold text-muted-foreground">
                            {task.creator.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[80px]">
                        {task.creator.name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                  )}
                </td>
                {/* Assignee */}
                <td className="px-4 py-3 text-center">
                  {task.assignees && task.assignees.length > 0 ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="flex items-center -space-x-1.5">
                        {task.assignees.slice(0, 3).map(a => (
                          <div
                            key={a.id}
                            title={a.name}
                            className="w-6 h-6 rounded-full border-2 border-board-column bg-muted overflow-hidden flex-shrink-0"
                          >
                            {a.avatar ? (
                              <img src={a.avatar} alt={a.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="w-full h-full flex items-center justify-center text-[10px] font-semibold text-muted-foreground">
                                {a.name.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {task.assignees[0] && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[80px]">
                          {task.assignees[0].name}
                          {task.assignees.length > 1 && (
                            <span className="text-gray-400 dark:text-gray-500 ml-1">
                              +{task.assignees.length - 1}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                  )}
                </td>
                {/* Upvotes */}
                {enableUpvotes && (
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleUpvote?.(task.id);
                      }}
                      className={`inline-flex items-center justify-center gap-1.5 px-2.5 h-7 rounded-lg border transition-all active:scale-95 cursor-pointer shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${
                        Boolean(currentUserId && task.upvotedBy?.includes(currentUserId))
                          ? "bg-workspace-accent/10 border-workspace-accent text-workspace-accent shadow-xs"
                          : "bg-white dark:bg-card border-slate-200 dark:border-slate-700/80 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                      }`}
                      title={Boolean(currentUserId && task.upvotedBy?.includes(currentUserId)) ? "Remove upvote" : "Upvote task"}
                    >
                      <svg
                        viewBox="0 0 24 22"
                        fill={Boolean(currentUserId && task.upvotedBy?.includes(currentUserId)) ? "currentColor" : "none"}
                        className={`w-3 h-3 transition-colors ${
                          Boolean(currentUserId && task.upvotedBy?.includes(currentUserId))
                            ? "text-workspace-accent fill-workspace-accent/20"
                            : "text-slate-600 dark:text-slate-400"
                        }`}
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 4.5l7.5 13a1.5 1.5 0 0 1-1.3 2.2H5.8a1.5 1.5 0 0 1-1.3-2.2L12 4.5z" />
                      </svg>
                      <span
                        className={`text-xs font-bold leading-none tabular-nums ${
                          Boolean(currentUserId && task.upvotedBy?.includes(currentUserId))
                            ? "text-workspace-accent"
                            : "text-slate-900 dark:text-slate-100"
                        }`}
                      >
                        {task.upvotes ?? (task.upvotedBy?.length || 0)}
                      </span>
                    </button>
                  </td>
                )}
                {/* Actions */}
                <td className="px-2 py-3 text-center">
                  <div className="flex items-center justify-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="p-1.5 rounded-md text-gray-400 dark:text-gray-500 hover:text-foreground hover:bg-muted transition-colors focus:outline-none"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onArchive?.(task.id);
                          }}
                        >
                          <Archive className="w-3.5 h-3.5" />
                          Archive
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          destructive
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.(task.id);
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
